import { timestamp, type Timestamp } from '../room/types.js'

/**
 * Who someone is, as far as this application is concerned.
 *
 * Deliberately no email address. Google's `sub` is a stable opaque identifier
 * scoped to this one app, which is everything we need to recognise a returning
 * player — and the safest place for a personal detail is somewhere it was never
 * written down.
 */
export interface SessionPayload {
  /** Google's `sub`. Opaque, stable, and unique per application. */
  readonly userId: string
  readonly name: string
  readonly avatar: string | null
  readonly expiresAt: Timestamp
}

export const SESSION_COOKIE = 'chess_session'
export const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1_000

const encoder = new TextEncoder()
const decoder = new TextDecoder()

const encodeBase64Url = (bytes: Uint8Array): string => {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

const decodeBase64Url = (text: string): Uint8Array<ArrayBuffer> | null => {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) return null
  const padded = text.replaceAll('-', '+').replaceAll('_', '/')
  try {
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
    return bytes
  } catch {
    return null
  }
}

const keyFor = async (secret: string): Promise<CryptoKey> =>
  crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ])

/**
 * A session is the payload plus an HMAC over it: `<base64url body>.<base64url mac>`.
 *
 * No server-side session table, so no database round trip on every socket
 * connection and every API call. The price is that a session cannot be revoked
 * before it expires, which for a casual chess site is the right side of that
 * trade — and it is the reason the TTL is measured in weeks rather than years.
 */
export const signSession = async (payload: SessionPayload, secret: string): Promise<string> => {
  const body = encodeBase64Url(encoder.encode(JSON.stringify(payload)))
  const mac = await crypto.subtle.sign('HMAC', await keyFor(secret), encoder.encode(body))
  return `${body}.${encodeBase64Url(new Uint8Array(mac))}`
}

/** Everything a payload must look like before we are willing to believe it. */
const parsePayload = (json: string): SessionPayload | null => {
  let value: unknown
  try {
    value = JSON.parse(json)
  } catch {
    return null
  }
  if (typeof value !== 'object' || value === null) return null

  const { userId, name, avatar, expiresAt } = value as Record<string, unknown>
  if (typeof userId !== 'string' || userId === '') return null
  if (typeof name !== 'string') return null
  if (avatar !== null && typeof avatar !== 'string') return null
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return null

  return { userId, name, avatar, expiresAt: timestamp(expiresAt) }
}

/**
 * Null for anything we are not certain of: bad shape, bad signature, wrong
 * secret, or expired.
 *
 * The signature is checked *before* the body is parsed. Parsing first would
 * mean running a parser over bytes an attacker chose, which is the wrong order
 * to do those two things in even when the parser is `JSON.parse`.
 */
export const verifySession = async (
  token: string,
  secret: string,
  now: Timestamp,
): Promise<SessionPayload | null> => {
  const dot = token.indexOf('.')
  if (dot <= 0 || dot === token.length - 1) return null

  const body = token.slice(0, dot)
  const signature = decodeBase64Url(token.slice(dot + 1))
  if (signature === null) return null

  const valid = await crypto.subtle.verify(
    'HMAC',
    await keyFor(secret),
    signature,
    encoder.encode(body),
  )
  if (!valid) return null

  const decoded = decodeBase64Url(body)
  if (decoded === null) return null

  const payload = parsePayload(decoder.decode(decoded))
  if (payload === null) return null

  return payload.expiresAt <= now ? null : payload
}

/**
 * Google hands back a full name; we show the first word of it.
 *
 * "Shivam" belongs over a chessboard in a way "Shivam Dwivedi" does not, and
 * publishing someone's surname to every stranger they play is a decision that
 * should have to be made deliberately rather than by default.
 */
export const givenNameOf = (fullName: string, fallback = 'Player'): string => {
  const first = fullName.trim().split(/\s+/)[0] ?? ''
  return first === '' ? fallback : first
}
